import { Injectable } from '@angular/core';
import { Dictionary } from 'highcharts';
import { Restangular } from 'ngx-restangular';

export type ThatchCredentialMode = 'legacy' | 'reference' | 'missing' | 'invalid';

export interface ThatchCredentialConfiguration {
  readonly type: 'thatch';
  readonly mode: ThatchCredentialMode;
  readonly configured: boolean;
}

export interface ThatchNonSecretCredentials {
  readonly apiUrl?: string;
}

export interface ThatchNonSecretConfig {
  readonly launchUrl?: string;
  readonly credentials?: ThatchNonSecretCredentials;
}

export interface ThatchVendorDto {
  readonly id: string;
  readonly vendorSlug: 'thatch';
  readonly config: ThatchNonSecretConfig;
  readonly credentialConfiguration: ThatchCredentialConfiguration;
}

export interface ThatchVendorMutation {
  readonly vendorSlug?: 'thatch';
  readonly config: ThatchNonSecretConfig;
}

export interface Vendor {
  id: string;
  vendorSlug: string;
  config: Dictionary<any>;
  metadata: Dictionary<any>;
  schedules: Dictionary<any>;
  adapter?: Dictionary<any>;
  credentialConfiguration?: ThatchCredentialConfiguration;
}

export interface VendorRequiredFields {
  id: string;
  vendorSlug: string;
  requiredUserFields: string[];
  requiredPrimaryFields: string[];
  requiredDependentFields: string[];
  requiredValues: string[];
  requiredFormValues: string[];
  requiredPlanValues: string[];
}

export interface CarrierIntegrationRerunResult {
  jobId?: string;
  status?: string;
  vendorId?: string;
  vendorSlug?: string;
  vendorProductCount?: number;
  queuedAt?: string;
}

@Injectable({
  providedIn: 'root'
})

export class VendorsService {

  constructor(
    private restangular: Restangular,
  ) {
  }

  createVendor(vendorData: Record<string, any>): Promise<Vendor> {
    return this.restangular.one('vendors').customPOST(vendorData).toPromise() as Promise<Vendor>;
  }

  deleteVendor(vendorId: string): Promise<Vendor> {
    return this.restangular.one('vendors', vendorId).customDELETE().toPromise() as Promise<Vendor>;
  }

  getAllVendors(): Promise<Vendor[]> {
    return this.restangular.one('vendors').getList().toPromise() as Promise<Vendor[]>;
  }

  getVendorAdapterOptions(): Promise<Record<string, any>> {
    return this.restangular.one('vendors/adapter-options').get().toPromise() as Promise<Record<string, any>>;
  }

  getRequiredFields(): Promise<VendorRequiredFields[]> {
    return this.restangular.one('vendors/required-fields').getList().toPromise() as Promise<VendorRequiredFields[]>;
  }

  runVendorScheduleNow(vendorId: string, method: 'enroll' | 'payment'): Promise<void> {
    return this.restangular.one('vendors', vendorId).one('runftp', method).customPUT({}).toPromise() as Promise<void>;
  }

  rerunCarrierIntegration(vendorId: string, reason: string): Promise<CarrierIntegrationRerunResult> {
    return this.restangular
      .one('vendors', vendorId)
      .one('carrier-integration-reruns')
      .customPOST({ reason })
      .toPromise() as Promise<CarrierIntegrationRerunResult>;
  }

  updateVendor(vendorId: string, vendorData: Record<string, any>): Promise<Vendor[]> {
    return this.restangular.one('vendors', vendorId).customPUT(vendorData).toPromise() as Promise<Vendor[]>;
  }

  updateThatchVendor(vendorId: string, vendorData: ThatchVendorMutation): Promise<void> {
    return this.restangular.one('vendors', vendorId).customPUT(vendorData).toPromise() as Promise<void>;
  }
}
